import React, { useState, useEffect } from 'react';
import validate from 'validate.js';
import { connect } from 'react-redux';
import * as actions from 'redux/actions/stockOrderActions';
import * as actions_dashboard from 'redux/actions/dashboardActions';
import { Translate } from 'react-redux-i18n';
import InputDate from 'components/InputDate';

const schema = {
    symbol: {
        presence: { allowEmpty: false, message: 'is required' },
        length: { maximum: 300 },
    },
};

function resolveOpenPrice(price) {
    const n = Number(price);
    if (Number.isFinite(n) && n > 0) {
        return Number(n.toFixed(2));
    }
    return 0.01;
}

function buildInitialValues(props) {
    return {
        symbol: props.symbol || '',
        name: props.name || '',
        quantity: 1,
        open_price: resolveOpenPrice(props.price),
        market: props.kind || 'BUY',
        open_at: new Date().toISOString().split('T')[0],
        id: props.user_id,
    };
}

const FormDialog = (props) => {
    const [open, setOpen] = useState(false);
    const [dialogState, setDialogState] = useState({
        isValid: false,
        values: buildInitialValues(props),
        touched: {},
        errors: {},
    });

    const handleClickOpen = () => {
        if (!props.symbol) {
            return;
        }
        setOpen(true);
        setDialogState({
            isValid: false,
            values: buildInitialValues(props),
            touched: {},
            errors: {},
        });
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleBackdropClick = (event) => {
        if (event.target === event.currentTarget) {
            handleClose();
        }
    };

    useEffect(() => {
        const errors = validate(dialogState.values, schema);
        setDialogState((prev) => ({
            ...prev,
            isValid: !errors,
            errors: errors || {},
        }));
    }, [dialogState.values]);

    useEffect(() => {
        if (!open) {
            return;
        }

        setDialogState((prev) => {
            if (prev.touched.open_price) {
                return {
                    ...prev,
                    values: {
                        ...prev.values,
                        symbol: props.symbol || prev.values.symbol,
                        name: props.name || prev.values.name,
                        market: props.kind || prev.values.market,
                    },
                };
            }

            return {
                ...prev,
                values: {
                    ...prev.values,
                    symbol: props.symbol || prev.values.symbol,
                    name: props.name || prev.values.name,
                    market: props.kind || prev.values.market,
                    open_price: resolveOpenPrice(props.price),
                },
            };
        });
    }, [open, props.symbol, props.name, props.price, props.kind]);

    const handleChange = (event) => {
        const { name, value } = event.target || {};
        if (!name) {
            return;
        }

        setDialogState((prev) => ({
            ...prev,
            values: {
                ...prev.values,
                [name]: value,
            },
            touched: {
                ...prev.touched,
                [name]: true,
            },
        }));
    };

    const save = (share) => {
        if (!dialogState.values.symbol) {
            return;
        }

        const payload = {
            ...dialogState.values,
            quantity: Number(dialogState.values.quantity) || 1,
            open_price: resolveOpenPrice(dialogState.values.open_price),
            ...(share ? { share: true } : {}),
        };

        props.newItem(payload, props.token);
        props.loadListIndicator(props.user_id, props.token);
        setOpen(false);
    };

    const total = (
        (Number(dialogState.values.quantity) || 0) * resolveOpenPrice(dialogState.values.open_price)
    ).toFixed(2);

    const sideLabel = dialogState.values.market === 'SELL' ? 'Sell' : 'Buy';

    return (
        <div className="trade-entry-modal-trigger">
            <button
                type="button"
                className="btn btn-primary"
                onClick={handleClickOpen}
                disabled={!props.symbol}
            >
                {props.button_name}
            </button>
            {open && (
                <div className="dialog" role="presentation" onClick={handleBackdropClick}>
                    <div
                        className="dialog-content dialog-content--entry"
                        role="dialog"
                        aria-modal="true"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 className="dialog-title">
                            {props.button_name} — Stock Entry
                        </h2>
                        <p className="dialog-content-text">
                            Enter stock trade details ({sideLabel})
                        </p>

                        <div className="entry-form">
                            <div className="entry-form-section">
                                <div className="entry-field">
                                    <label htmlFor={`${props.button_name}-symbol`}>Symbol</label>
                                    <input
                                        id={`${props.button_name}-symbol`}
                                        name="symbol"
                                        type="text"
                                        value={dialogState.values.symbol}
                                        disabled
                                    />
                                </div>
                                <div className="entry-field">
                                    <label htmlFor={`${props.button_name}-name`}>Company</label>
                                    <input
                                        id={`${props.button_name}-name`}
                                        name="name"
                                        type="text"
                                        value={dialogState.values.name}
                                        disabled
                                    />
                                </div>
                            </div>

                            <div className="entry-form-grid entry-form-grid--4 entry-form-grid--stock-entry">
                                <div className="entry-field">
                                    <label htmlFor={`${props.button_name}-quantity`}>Quantity</label>
                                    <input
                                        id={`${props.button_name}-quantity`}
                                        name="quantity"
                                        type="number"
                                        min="1"
                                        required
                                        value={dialogState.values.quantity}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="entry-field entry-field--compact">
                                    <label htmlFor={`${props.button_name}-price`}>Price</label>
                                    <input
                                        id={`${props.button_name}-price`}
                                        name="open_price"
                                        type="number"
                                        min="0.01"
                                        step="0.01"
                                        required
                                        value={dialogState.values.open_price}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="entry-field entry-field--compact">
                                    <label htmlFor={`${props.button_name}-market`}>Side</label>
                                    <select
                                        id={`${props.button_name}-market`}
                                        name="market"
                                        disabled
                                        value={dialogState.values.market}
                                    >
                                        <option value="BUY">Buy</option>
                                        <option value="SELL">Sell</option>
                                    </select>
                                </div>
                                <div className="entry-field">
                                    <label htmlFor={`${props.button_name}-open_at`}>Open date</label>
                                    <InputDate
                                        id={`${props.button_name}-open_at`}
                                        name="open_at"
                                        required
                                        label=""
                                        value={dialogState.values.open_at}
                                        onChange={handleChange}
                                        min="1900-01-01"
                                        max={new Date().toISOString().split('T')[0]}
                                    />
                                </div>
                            </div>

                            <div className="entry-form-total">$ {total} USD</div>

                            <div className="dialog-actions">
                                <button type="button" className="btn btn-secondary" onClick={handleClose}>
                                    <Translate value="cancel" />
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    disabled={!dialogState.isValid}
                                    onClick={() => save(true)}
                                >
                                    <Translate value="save_share" />
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    disabled={!dialogState.isValid}
                                    onClick={() => save(false)}
                                >
                                    <Translate value="save" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

function mapStateToProps(reducer) {
    return {
        user_id: reducer.session.auth.id,
        token: reducer.session.auth.token,
    };
}

const mapDispatchToProps = (dispatch) => ({
    newItem: (params, token) =>
        dispatch(actions.newItem({ params, token })),
    loadListIndicator: (user_id, token) =>
        dispatch(actions_dashboard.loadListIndicator({ user_id, token })),
});

export default connect(mapStateToProps, mapDispatchToProps)(FormDialog);
